import { Link } from "@inertiajs/react"
import { CheckCircle2, Circle } from "lucide-react"

import type { PropertyDashboardChecklist } from "@/lib/types"

// Canonical go-live checklist labels. Keys MUST stay in sync with the
// PropertyChecklist concern (app/controllers/concerns/property_checklist.rb)
// and the PropertyDashboardChecklist type. Shared by the property dashboard
// and the onboarding completion screen so there is one source of truth.
export const CHECKLIST_LABELS: Record<keyof PropertyDashboardChecklist, string> = {
  logo_added:            "Logo added",
  wifi_configured:       "WiFi configured",
  inside_info_published: "INSIDE info published",
  default_guide_active:  "Default guide active",
  qr_generated:          "QR code generated",
  pois_count_ok:         "At least 5 POIs added",
  first_guest_session:   "First guest session received",
}

// A link per checklist item. Each page builds this from its own `urls` object,
// so the component stays decoupled from any specific urls shape.
export type ChecklistLinks = Record<keyof PropertyDashboardChecklist, string | null>

export type ChecklistProgress = {
  keys: Array<keyof PropertyDashboardChecklist>
  completedCount: number
  totalCount: number
  allComplete: boolean
  progressPct: number
}

export function checklistProgress(checklist: PropertyDashboardChecklist): ChecklistProgress {
  const keys = Object.keys(checklist) as Array<keyof PropertyDashboardChecklist>
  const completedCount = keys.filter((k) => checklist[k]).length
  const totalCount = keys.length
  return {
    keys,
    completedCount,
    totalCount,
    allComplete: completedCount === totalCount,
    progressPct: totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100),
  }
}

export function ChecklistItem({
  label,
  done,
  href,
}: {
  label: string
  done: boolean
  href: string | null
}) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      {done ? (
        <CheckCircle2 className="size-4 shrink-0 text-green-500" aria-hidden="true" />
      ) : (
        <Circle className="size-4 shrink-0 text-muted-foreground/50" aria-hidden="true" />
      )}
      <span className="sr-only">{done ? "Completed:" : "To do:"}</span>
      {done ? (
        <span className="text-sm text-muted-foreground line-through">{label}</span>
      ) : href ? (
        <Link href={href} className="text-sm hover:underline">
          {label}
        </Link>
      ) : (
        <span className="text-sm text-muted-foreground">{label}</span>
      )}
    </div>
  )
}

export function ChecklistProgressBar({ progressPct }: { progressPct: number }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full bg-green-500 transition-all"
        style={{ width: `${progressPct}%` }}
        role="progressbar"
        aria-label="Setup completion"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  )
}
